// D:\Casticart\frontend\src\components\admin\Sidebar.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMessageCount } from '../../contexts/MessageCountContext';
import { useAnnouncements } from '../../contexts/AnnouncementContext';
import { useBugReporting } from '../../contexts/BugReportingContext';
import { useAdminFeatureFlags } from '../../contexts/AdminFeatureFlagsContext';
import { hasExactAdminRole, resolveAdminPermission } from '../../constants/adminPermissionCatalog';
import { setLastLoginRole } from '../../utils/lastLoginRole';
import { adminLogout } from '../../api/auth';
import { useAdminMenuBadges } from '../../contexts/AdminMenuBadgeContext';
import { listAllMyTaskProjects } from '../../api/taskEvents';
import { useAdminChatFollowupSummary } from '../common/chat/followups/chatFollowupApi';
import AiFluidMark from '../common/chat/shared/AiFluidMark';
import {
  buildAdminChatMenuBadges,
  formatMenuBadgeCount,
} from '../../utils/adminChatMenuBadges';
import { resolveAdminAssignmentBadgeToneClass } from '../../utils/adminAssignmentBadgeTone';
import { SectionLabel, SidebarBottom, SystemSettingsSection, TaskSidebarSection } from './SidebarSections';

const SIDEBAR_SCROLL_KEY = 'admin-sidebar-scroll-top';
const SIDEBAR_MODE_KEY = 'admin-sidebar-mode';
const CREATE_SHORTCUT_LONG_PRESS_DELAY_MS = 560;
const SIDEBAR_DISCLOSURE_ANIMATION_MS = 180;
const SIDEBAR_DASHBOARD_PICKER_HOVER_DELAY_MS = 300;

function SidebarItemWithCreateShortcut({ children, shortcut }) {
  const navigate = useNavigate();
  const longPressRef = useRef({ timer: null, ignoreClick: false, clearTimer: null });

  const clearLongPress = useCallback(() => {
    const state = longPressRef.current;
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
  }, []);

  const startLongPress = useCallback((event) => {
    if (!shortcut) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const state = longPressRef.current;
    clearLongPress();
    state.ignoreClick = false;
    state.timer = setTimeout(() => {
      state.timer = null;
      state.ignoreClick = true;
      navigate(shortcut.to, shortcut.state ? { state: shortcut.state } : undefined);
      if (state.clearTimer) clearTimeout(state.clearTimer);
      state.clearTimer = setTimeout(() => {
        state.ignoreClick = false;
        state.clearTimer = null;
      }, 1000);
    }, CREATE_SHORTCUT_LONG_PRESS_DELAY_MS);
  }, [clearLongPress, navigate, shortcut]);

  const handleClickCapture = useCallback((event) => {
    if (!longPressRef.current.ignoreClick) return;
    longPressRef.current.ignoreClick = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(
    () => () => {
      clearLongPress();
      if (longPressRef.current.clearTimer) {
        clearTimeout(longPressRef.current.clearTimer);
      }
    },
    [clearLongPress]
  );

  if (!shortcut) {
    return <li className="sidebar-item">{children}</li>;
  }

  return (
    <li
      className="sidebar-item sidebar-item--has-create"
      onPointerDown={startLongPress}
      onPointerUp={clearLongPress}
      onPointerLeave={clearLongPress}
      onPointerCancel={clearLongPress}
      onClickCapture={handleClickCapture}
    >
      {children}
    </li>
  );
}

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const restorePendingRef = useRef(true);
  const { user } = useAuth();
  const { pendingApprovalCount } = useAnnouncements();
  const {
    adminMessageCount,
    eventChatMessageCount,
    adminGroupChatMessageCount,
    adminArtistChatTone,
    adminEventChatTone,
    adminGroupChatTone,
    eventChatHasMention,
    groupChatHasMention,
    conciergePendingCount,
    refreshConciergePendingCount,
  } = useMessageCount();
  const { enabled: bugReportingEnabled } = useBugReporting();
  const { loading: featureFlagsLoading, isEnabled: isFeatureEnabled } = useAdminFeatureFlags();
  const perms = useMemo(
    () => (Array.isArray(user?.permissions) ? user.permissions : []),
    [user?.permissions]
  );
  const isTaskOnlyAccount = user?.userType === 'admin' && user?.role === 'task_only';
  const isExternalAccount = user?.is_external_account === true;
  const isExactAdminRole = hasExactAdminRole(user?.role);
  const isSuperAdmin =
    user?.role === 'admin' ||
    (user?.userType === 'admin' &&
      (user?.permissions === 'admin' ||
        user?.permissions?.includes?.('admin') ||
        user?.permissions?.includes?.('*')));
  const can = useCallback(
    (permissionKey) =>
      isSuperAdmin ||
      resolveAdminPermission({
        role: user?.role || 'general',
        isExternalAccount,
        permissions: perms,
        permissionKey,
      }),
    [isExternalAccount, isSuperAdmin, perms, user?.role]
  );
  const hasConciergePermission = can('CAN_MANAGE_CONCIERGE') || can('NAV_CONCIERGE');
  const canManagePolicies = can('CAN_EDIT_POLICIES') || can('NAV_POLICIES');
  const canViewActionLogs = can('CAN_VIEW_ACTION_LOGS') || can('NAV_ACTION_LOGS');
  const canViewArtists = can('NAV_ARTISTS');
  const canViewEvents = can('NAV_EVENTS');
  const canRegisterEvents = can('NAV_EVENT_REGISTER') || can('EVENT_CREATE');
  const canViewMusicLibrary = can('NAV_MUSIC_LIBRARY');
  const canViewVenues = can('NAV_VENUES');
  const canViewArtistChat = can('NAV_PERSONAL_CHAT');
  const canViewEventChat = can('NAV_EVENT_CHAT');
  const canViewGroupChat = can('NAV_GROUP_CHAT');
  const canBroadcast = can('NAV_CHAT_BROADCAST');
  const canViewCastingOffer = can('NAV_CASTING_OFFER');
  const canViewCastingContract = can('NAV_CASTING_CONTRACT');
  const canViewTicketSales = can('NAV_TICKET_SALES');
  const canViewFinance = can('NAV_FINANCE');
  const canViewPayments = can('NAV_PAYMENTS');
  const canViewTasks = can('NAV_TASKS');
  const canViewAnnouncements = can('NAV_ANNOUNCEMENTS');
  const canViewOperationalEmails = can('NAV_OPERATIONAL_EMAILS');
  const canManageMobileRelease = can('SYSTEM_MANAGE_MOBILE_RELEASES');
  const canManageMacRelease = can('SYSTEM_MANAGE_MAC_RELEASES');
  const canManageAccounts = isSuperAdmin || can('NAV_ACCOUNT_MANAGEMENT') || can('SYSTEM_MANAGE_ACCOUNTS');
  const canViewBugs = bugReportingEnabled && can('NAV_BUGS');
  const aiPlannerEnabled = !featureFlagsLoading && isFeatureEnabled('ai_planner');
  const canViewAiPlanner = aiPlannerEnabled && can('NAV_AI_PLANNER');
  const shouldLoadChatFollowupBadge = canViewArtistChat || canViewEventChat || canViewGroupChat;
  const { summary: chatFollowupSummary } = useAdminChatFollowupSummary({
    enabled: shouldLoadChatFollowupBadge,
  });
  const chatMenuBadges = useMemo(
    () =>
      buildAdminChatMenuBadges({
        artistUnreadCount: adminMessageCount,
        artistUnreadTone: adminArtistChatTone,
        eventUnreadCount: eventChatMessageCount,
        eventUnreadTone: adminEventChatTone,
        eventHasMention: eventChatHasMention,
        groupUnreadCount: adminGroupChatMessageCount,
        groupUnreadTone: adminGroupChatTone,
        groupHasMention: groupChatHasMention,
        followupSummary: chatFollowupSummary,
      }),
    [
      adminArtistChatTone,
      adminEventChatTone,
      adminGroupChatMessageCount,
      adminGroupChatTone,
      adminMessageCount,
      chatFollowupSummary,
      eventChatMessageCount,
      eventChatHasMention,
      groupChatHasMention,
    ]
  );
  const showArtistChatBadge = chatMenuBadges.artist.hasBadge;
  const showEventChatBadge = chatMenuBadges.event.hasBadge;
  const showGroupChatBadge = chatMenuBadges.group.hasBadge;
  const conciergeBadgeToneClass =
    hasConciergePermission ? 'sidebar-count-badge--alert' : 'sidebar-count-badge--ok';
  const [sidebarMode, setSidebarMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = window.sessionStorage.getItem(SIDEBAR_MODE_KEY);
      if (saved === 'event' || saved === 'task') return saved;
    }
    return location.pathname.startsWith('/admin/workspaces/task-management/dashboard') ? 'task' : 'event';
  });
  const [isDashboardPickerOpen, setIsDashboardPickerOpen] = useState(false);
  const [isDashboardPickerClosing, setIsDashboardPickerClosing] = useState(false);
  const [dashboardPickerOpenReason, setDashboardPickerOpenReason] = useState(null);
  const dashboardPickerRef = useRef(null);
  const dashboardPickerOpenTimerRef = useRef(null);
  const dashboardPickerCloseTimerRef = useRef(null);
  const dashboardPickerAnimationTimerRef = useRef(null);
  const [taskProjects, setTaskProjects] = useState([]);
  const [taskProjectsLoading, setTaskProjectsLoading] = useState(false);
  const [taskProjectsError, setTaskProjectsError] = useState('');
  const {
    offerTaskCount, offerTaskAssignedCount, contractTaskCount, contractTaskAssignedCount,
    invoiceUndownloadedCount, invoiceUndownloadedTone,
  } = useAdminMenuBadges();
  const effectiveSidebarMode =
    isTaskOnlyAccount || (!canViewEvents && canViewTasks) ? 'task' : sidebarMode;
  const dashboardPath =
    effectiveSidebarMode === 'task' ? '/admin/workspaces/task-management/dashboard' : '/admin/workspaces/event-operations/dashboard';
  const dashboardIsActive =
    effectiveSidebarMode === 'task'
      ? location.pathname === '/admin/workspaces/task-management/dashboard'
      : location.pathname === '/admin/workspaces/event-operations/dashboard';
  // ダッシュボード切替（ワークスペースピッカー）。将来ダッシュボード種別が増えても
  // この配列に足すだけでスケールする。選択状態はサイドメニューの選択色に揃える。
  const dashboardOptions = [
    { key: 'event', label: 'イベント', iconClass: 'fa-solid fa-calendar-days', disabled: !canViewEvents },
    { key: 'task', label: 'タスク', iconClass: 'fa-solid fa-list-check', disabled: !canViewTasks },
  ];
  const currentDashboard =
    dashboardOptions.find((opt) => opt.key === effectiveSidebarMode) || dashboardOptions[0];
  const createShortcuts = useMemo(
    () => ({
      artists: canViewArtists
        ? { to: '/admin/workspaces/artist-management/artists/register', label: '新規登録' }
        : null,
      events: canRegisterEvents
        ? { to: '/admin/workspaces/event-operations/events/register', label: '新規登録' }
        : null,
      venues: canViewVenues
        ? { to: '/admin/workspaces/event-operations/venues/register', label: '新規登録' }
        : null,
      offers: canViewCastingOffer
        ? { to: '/admin/workspaces/casting/offers/new', label: '新規作成' }
        : null,
      contracts: canViewCastingContract
        ? {
            to: '/admin/workspaces/contracts/form',
            state: { reset: true },
            label: '新規作成',
          }
        : null,
    }),
    [
      canRegisterEvents,
      canViewArtists,
      canViewCastingContract,
      canViewCastingOffer,
      canViewVenues,
    ]
  );
  const taskLinkedEventCount = useMemo(() => {
    const eventUuidSet = new Set();
    taskProjects.forEach((project) => {
      const eventUuid = String(project?.eventUuid || '').trim();
      if (eventUuid) eventUuidSet.add(eventUuid);
    });
    return eventUuidSet.size;
  }, [taskProjects]);

  const isActivePath = (path, { exact = false, exclude = [] } = {}) => {
    if (!path) return false;
    if (Array.isArray(exclude) && exclude.some((prefix) => location.pathname.startsWith(prefix))) {
      return false;
    }
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  const castingOfferIsActive = isActivePath('/admin/workspaces/casting/offers');
  const castingContractIsActive =
    isActivePath('/admin/workspaces/contracts/history', { exact: true }) ||
    isActivePath('/admin/workspaces/contracts/form') ||
    isActivePath('/admin/workspaces/contracts/confirm', { exact: true }) ||
    isActivePath('/admin/workspaces/contracts/success', { exact: true }) ||
    isActivePath('/admin/workspaces/contracts');

  const isMobileViewport = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 700px)').matches;

  const closeMobileSidebar = () => {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('sidebar-mobile-open');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('casticart:sidebar-visibility', { detail: { visible: false } })
      );
    }
  };

  const handleSidebarNav = (event) => {
    persistSidebarScroll();
    if (!isMobileViewport()) return;
    const modeSwitchButton = event.target.closest('.sidebar-ws-picker button');
    if (modeSwitchButton) return;
    const interactive = event.target.closest('a, button, .sidebar-item.logout');
    if (interactive) closeMobileSidebar();
  };

  const handleChatMenuClick = (event, chatType) => {
    persistSidebarScroll();
    if (event) {
      if (
        event.defaultPrevented ||
        (event.button && event.button !== 0) ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }
      event.preventDefault();
    }

    const destination =
      chatType === 'event' ? '/admin/workspaces/event-operations/event-chat' : '/admin/workspaces/communications/artist-chat';

    navigate(destination, {
      state: {
        reset: Date.now(),
        chatType,
      },
    });

    if (isMobileViewport()) {
      closeMobileSidebar();
    }
  };

  const handleSidebarModeChange = (nextMode) => {
    if (nextMode !== 'event' && nextMode !== 'task') return;
    if (nextMode === 'task' && !canViewTasks) return;
    setSidebarMode(nextMode);
    const destination = nextMode === 'task' ? '/admin/workspaces/task-management/dashboard' : '/admin/workspaces/event-operations/dashboard';
    if (location.pathname !== destination) {
      navigate(destination);
    }
    if (isMobileViewport()) {
      closeMobileSidebar();
    }
  };

  const clearDashboardPickerTimers = useCallback(() => {
    if (dashboardPickerOpenTimerRef.current) {
      clearTimeout(dashboardPickerOpenTimerRef.current);
      dashboardPickerOpenTimerRef.current = null;
    }
    if (dashboardPickerCloseTimerRef.current) {
      clearTimeout(dashboardPickerCloseTimerRef.current);
      dashboardPickerCloseTimerRef.current = null;
    }
  }, []);

  const clearDashboardPickerAnimationTimer = useCallback(() => {
    if (dashboardPickerAnimationTimerRef.current) {
      clearTimeout(dashboardPickerAnimationTimerRef.current);
      dashboardPickerAnimationTimerRef.current = null;
    }
  }, []);

  const openDashboardPickerNow = useCallback((reason = 'click') => {
    clearDashboardPickerTimers();
    clearDashboardPickerAnimationTimer();
    setIsDashboardPickerClosing(false);
    setDashboardPickerOpenReason(reason);
    setIsDashboardPickerOpen(true);
  }, [clearDashboardPickerAnimationTimer, clearDashboardPickerTimers]);

  const closeDashboardPickerNow = useCallback(() => {
    clearDashboardPickerTimers();
    clearDashboardPickerAnimationTimer();
    setDashboardPickerOpenReason(null);
    setIsDashboardPickerClosing(true);
    dashboardPickerAnimationTimerRef.current = setTimeout(() => {
      dashboardPickerAnimationTimerRef.current = null;
      setIsDashboardPickerOpen(false);
      setIsDashboardPickerClosing(false);
    }, SIDEBAR_DISCLOSURE_ANIMATION_MS);
  }, [clearDashboardPickerAnimationTimer, clearDashboardPickerTimers]);

  // ポインタがトリガー/メニューに入ったら閉じる予約を取り消し、閉じていれば0.3秒後に開く。
  // メニューはトリガーの子要素なので、両者にまたがってホバーしても開いたまま保たれる。
  const handleDashboardPickerPointerEnter = useCallback(() => {
    if (dashboardPickerCloseTimerRef.current) {
      clearTimeout(dashboardPickerCloseTimerRef.current);
      dashboardPickerCloseTimerRef.current = null;
    }
    if (isDashboardPickerOpen) {
      clearDashboardPickerAnimationTimer();
      setIsDashboardPickerClosing(false);
      if (dashboardPickerOpenReason !== 'click') {
        setDashboardPickerOpenReason('hover');
      }
      return;
    }
    if (dashboardPickerOpenTimerRef.current) return;
    dashboardPickerOpenTimerRef.current = setTimeout(() => {
      dashboardPickerOpenTimerRef.current = null;
      openDashboardPickerNow('hover');
    }, SIDEBAR_DASHBOARD_PICKER_HOVER_DELAY_MS);
  }, [
    clearDashboardPickerAnimationTimer,
    dashboardPickerOpenReason,
    isDashboardPickerOpen,
    openDashboardPickerNow,
  ]);

  // ポインタが外れたら、隙間を横切る一瞬の離脱で閉じないよう猶予（180ms）を置いてから閉じる。
  // 再びトリガー/メニューに入れば上の enter 側で予約が取り消される。
  const handleDashboardPickerPointerLeave = useCallback(() => {
    if (dashboardPickerOpenTimerRef.current) {
      clearTimeout(dashboardPickerOpenTimerRef.current);
      dashboardPickerOpenTimerRef.current = null;
    }
    if (dashboardPickerCloseTimerRef.current) {
      clearTimeout(dashboardPickerCloseTimerRef.current);
    }
    if (dashboardPickerOpenReason !== 'hover') return;
    dashboardPickerCloseTimerRef.current = setTimeout(() => {
      dashboardPickerCloseTimerRef.current = null;
      closeDashboardPickerNow();
    }, SIDEBAR_DISCLOSURE_ANIMATION_MS);
  }, [closeDashboardPickerNow, dashboardPickerOpenReason]);

  const handleDashboardOptionSelect = (opt) => {
    if (!opt || opt.disabled) return;
    handleSidebarModeChange(opt.key);
    closeDashboardPickerNow();
  };

  const handleQuickTaskCompose = () => {
    if (!canViewTasks || typeof window === 'undefined') return;
    persistSidebarScroll();
    window.dispatchEvent(new CustomEvent('casticart:quick-task-compose'));
    if (isMobileViewport()) {
      closeMobileSidebar();
    }
  };

  useEffect(() => {
    if (!isDashboardPickerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeDashboardPickerNow();
    };
    const onPointerDown = (event) => {
      if (dashboardPickerRef.current && !dashboardPickerRef.current.contains(event.target)) {
        closeDashboardPickerNow();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [isDashboardPickerOpen, closeDashboardPickerNow]);

  useEffect(
    () => () => {
      clearDashboardPickerTimers();
      clearDashboardPickerAnimationTimer();
    },
    [clearDashboardPickerAnimationTimer, clearDashboardPickerTimers]
  );

  const handleLogout = async () => {
    try {
      const { ok, data } = await adminLogout();
      if (ok) {
        setLastLoginRole('admin');
        window.dispatchEvent(new CustomEvent('casticart:logout', { detail: { role: 'admin' } }));
        navigate(data?.redirect && data.redirect !== '/' ? data.redirect : '/admin-login');
      } else {
        throw new Error('Logout failed');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // 経理ダッシュボードのデフォルトリンク（今年）
  const financeLink = ({ year, month = null } = {}) => {
    const y = Number.isInteger(year) ? year : new Date().getFullYear();
    const params = new URLSearchParams();
    params.set('y', String(y));
    if (Number.isInteger(month)) {
      const m = Math.min(12, Math.max(1, month));
      params.set('m', String(m));
    }
    const query = params.toString();
    return `/admin/workspaces/finance/dashboard${query ? `?${query}` : ''}`;
  };

  const fetchTaskProjects = useCallback(async () => {
    if (user?.userType !== 'admin' || !canViewTasks) {
      setTaskProjects([]);
      setTaskProjectsError('');
      setTaskProjectsLoading(false);
      return;
    }
    try {
      setTaskProjectsLoading(true);
      setTaskProjectsError('');
      const projects = await listAllMyTaskProjects();
      setTaskProjects(Array.isArray(projects) ? projects : []);
    } catch (error) {
      setTaskProjects([]);
      setTaskProjectsError(error?.message || '参加プロジェクトの取得に失敗しました。');
    } finally {
      setTaskProjectsLoading(false);
    }
  }, [canViewTasks, user?.userType]);

  useEffect(() => {
    restorePendingRef.current = true;
  }, [location.key]);

  const persistSidebarScroll = useCallback(() => {
    if (typeof window === 'undefined') return;
    const node = sidebarRef.current;
    if (!node) return;
    window.sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(node.scrollTop));
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (!restorePendingRef.current) return;
    const node = sidebarRef.current;
    if (!node) return;

    const saved = window.sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (saved == null) {
      restorePendingRef.current = false;
      return;
    }

    const scrollTop = Number(saved);
    if (!Number.isFinite(scrollTop)) {
      restorePendingRef.current = false;
      return;
    }

    if (scrollTop > 0 && node.scrollHeight <= node.clientHeight) {
      return;
    }

    node.scrollTop = scrollTop;
    restorePendingRef.current = false;
  }, [
    aiPlannerEnabled,
    bugReportingEnabled,
    canManageMobileRelease,
    canManagePolicies,
    canViewActionLogs,
    isSuperAdmin,
    location.key,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = sidebarRef.current;
    if (!node) return;

    const handleScroll = () => {
      persistSidebarScroll();
    };

    node.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      persistSidebarScroll();
      node.removeEventListener('scroll', handleScroll);
    };
  }, [persistSidebarScroll]);

  useEffect(() => {
    const onRefresh = () => refreshConciergePendingCount();
    window.addEventListener('concierge:refresh-pending-count', onRefresh);
    return () => window.removeEventListener('concierge:refresh-pending-count', onRefresh);
  }, [refreshConciergePendingCount]);

  useEffect(() => {
    const onPeerNotification = (event) => {
      const detail = event?.detail || {};
      if (detail?.type === 'concierge.application') refreshConciergePendingCount();
    };
    window.addEventListener('peer:notification', onPeerNotification);
    return () => window.removeEventListener('peer:notification', onPeerNotification);
  }, [refreshConciergePendingCount]);

  useEffect(() => {
    if (isTaskOnlyAccount) {
      setSidebarMode('task');
      return;
    }
    if (location.pathname.startsWith('/admin/workspaces/task-management/dashboard')) {
      setSidebarMode('task');
      return;
    }
    if (location.pathname === '/admin/workspaces/event-operations/dashboard') {
      setSidebarMode('event');
    }
  }, [isTaskOnlyAccount, location.pathname]);

  useEffect(() => {
    if (effectiveSidebarMode !== 'task' && !location.pathname.startsWith('/admin/workspaces/task-management/dashboard')) return;
    fetchTaskProjects();
  }, [effectiveSidebarMode, fetchTaskProjects, location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(SIDEBAR_MODE_KEY, effectiveSidebarMode);
  }, [effectiveSidebarMode]);

  return (
    <aside className="sidebar" onClick={handleSidebarNav} ref={sidebarRef}>
      {!isTaskOnlyAccount ? (
        <div
          className={`sidebar-ws-picker${isDashboardPickerOpen ? ' is-open' : ''}`}
          ref={dashboardPickerRef}
          onMouseEnter={handleDashboardPickerPointerEnter}
          onMouseLeave={handleDashboardPickerPointerLeave}
        >
          <button
            type="button"
            className="sidebar-ws-trigger"
            aria-haspopup="listbox"
            aria-expanded={isDashboardPickerOpen}
            aria-label={`ダッシュボード切替、現在: ${currentDashboard.label}`}
            onClick={() => {
              if (isDashboardPickerOpen && dashboardPickerOpenReason === 'click') {
                closeDashboardPickerNow();
              } else {
                openDashboardPickerNow('click');
              }
            }}
          >
            <span className="sidebar-ws-trigger__main">
              <i className={`sidebar-ws-trigger__icon ${currentDashboard.iconClass}`} aria-hidden="true" />
              <span className="sidebar-ws-trigger__label">{currentDashboard.label}</span>
            </span>
            <i className="sidebar-ws-trigger__chevron fa-solid fa-chevron-down" aria-hidden="true" />
          </button>
          {isDashboardPickerOpen ? (
            <ul
              className={`sidebar-ws-menu${isDashboardPickerClosing ? ' is-closing' : ''}`}
              role="listbox"
              aria-label="管理メニュー切替"
            >
              {dashboardOptions.map((opt) => {
                const selected = opt.key === effectiveSidebarMode;
                return (
                  <li key={opt.key} role="option" aria-selected={selected}>
                    <button
                      type="button"
                      className={`sidebar-ws-option${selected ? ' is-active' : ''}`}
                      disabled={opt.disabled}
                      onClick={() => handleDashboardOptionSelect(opt)}
                    >
                      <i className={`sidebar-ws-option__icon ${opt.iconClass}`} aria-hidden="true" />
                      <span>{opt.label}</span>
                      {selected ? (
                        <i className="sidebar-ws-option__check fa-solid fa-check" aria-hidden="true" />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}

      <ul>
        <li className="sidebar-item">
          <Link
            to={dashboardPath}
            className={dashboardIsActive ? 'is-active' : ''}
          >
            <span className="sidebar-item__content">
              <i className="fa-solid fa-grip sidebar-item__icon" aria-hidden="true" />
              <span>ダッシュボード</span>
            </span>
          </Link>
        </li>
        {canViewAiPlanner ? (
          <li className="sidebar-item">
            <Link
              to="/admin/workspaces/service-ai"
              className={
                isActivePath('/admin/workspaces/service-ai', { exact: true }) ||
                isActivePath('/admin/workspaces/planning/ai-planner', { exact: true })
                  ? 'is-active'
                  : ''
              }
            >
              <span className="sidebar-item__content">
                <AiFluidMark variant="tiny" className="ai-fluid-mark--static" />
                <span>Casticart AI</span>
              </span>
            </Link>
          </li>
        ) : null}
        {canViewTasks ? (
          <li className="sidebar-item">
            <button
              type="button"
              className="sidebar-item__quickTask"
              onClick={handleQuickTaskCompose}
            >
              <span className="sidebar-item__content">
                <i className="fa-solid fa-plus sidebar-item__icon" aria-hidden="true" />
                <span>クイックタスク作成</span>
              </span>
            </button>
          </li>
        ) : null}
      </ul>

      {effectiveSidebarMode === 'task' && canViewTasks ? (
        <TaskSidebarSection
          isTaskOnlyAccount={isTaskOnlyAccount}
          taskLinkedEventCount={taskLinkedEventCount}
          taskProjects={taskProjects}
          taskProjectsLoading={taskProjectsLoading}
          taskProjectsError={taskProjectsError}
          currentPath={location.pathname}
          isActivePath={isActivePath}
        />
      ) : (
        <>
          {canViewArtists && (
            <>
          <SectionLabel iconClass="fa-solid fa-user" text="アーティスト管理" />
          <ul>
            <SidebarItemWithCreateShortcut shortcut={createShortcuts.artists}>
              <Link
                to="/admin/workspaces/artist-management/artists"
                className={isActivePath('/admin/workspaces/artist-management/artists', { exact: true }) ? 'is-active' : ''}
              >
                <span>アーティスト</span>
              </Link>
            </SidebarItemWithCreateShortcut>
          </ul>
            </>
          )}

          {(canViewEvents || canViewMusicLibrary || canViewVenues) && (
            <>
          <SectionLabel iconClass="fa-solid fa-calendar" text="イベント管理" />
          <ul>
            {canViewEvents && (
            <SidebarItemWithCreateShortcut shortcut={createShortcuts.events}>
              <Link
                to="/admin/workspaces/event-operations/events"
                className={
                  isActivePath('/admin/workspaces/event-operations/events', {
                    exclude: ['/admin/workspaces/event-operations/events/register'],
                  })
                    ? 'is-active'
                    : ''
                }
              >
                <span>イベント</span>
              </Link>
            </SidebarItemWithCreateShortcut>
            )}
            {canViewMusicLibrary && (
            <li className="sidebar-item">
              <Link
                to="/admin/workspaces/library/music"
                className={isActivePath('/admin/workspaces/library/music', { exact: true }) ? 'is-active' : ''}
              >
                <span>楽曲管理</span>
              </Link>
            </li>
            )}
            {canViewVenues && (
            <SidebarItemWithCreateShortcut shortcut={createShortcuts.venues}>
              <Link
                to="/admin/workspaces/event-operations/venues"
                className={
                  isActivePath('/admin/workspaces/event-operations/venues', {
                    exclude: ['/admin/workspaces/event-operations/venues/register'],
                  })
                    ? 'is-active'
                    : ''
                }
              >
                <span>会場</span>
              </Link>
            </SidebarItemWithCreateShortcut>
            )}
          </ul>
            </>
          )}

          {(canViewArtistChat || canViewEventChat || canViewGroupChat || canBroadcast) && (
            <>
          <SectionLabel iconClass="fa-solid fa-comment" text="メッセージ" />
          <ul>
            {canViewArtistChat && (
            <li className="sidebar-item">
              <Link
                to="/admin/workspaces/communications/artist-chat"
                onClick={(event) => handleChatMenuClick(event, 'artist')}
                className={isActivePath('/admin/workspaces/communications/artist-chat', { exact: true }) ? 'is-active' : ''}
              >
                アーティストチャット
                {showArtistChatBadge && (
                  <span className={`sidebar-count-badge ${chatMenuBadges.artist.toneClassName}`}>
                    {formatMenuBadgeCount(chatMenuBadges.artist.count)}
                  </span>
                )}
              </Link>
            </li>
            )}
            {canViewEventChat && (
            <li className="sidebar-item">
              <Link
                to="/admin/workspaces/event-operations/event-chat"
                onClick={(event) => handleChatMenuClick(event, 'event')}
                className={isActivePath('/admin/workspaces/event-operations/event-chat', { exact: true }) ? 'is-active' : ''}
              >
                イベントチャット
                {showEventChatBadge && (
                  <span className={`sidebar-count-badge ${chatMenuBadges.event.toneClassName}`}>
                    {formatMenuBadgeCount(chatMenuBadges.event.count)}
                  </span>
                )}
              </Link>
            </li>
            )}
            {canViewGroupChat && (
            <li className="sidebar-item">
              <Link
                to="/admin/workspaces/communications/group-chat"
                className={isActivePath('/admin/workspaces/communications/group-chat', { exact: true }) ? 'is-active' : ''}
              >
                グループチャット
                {showGroupChatBadge && (
                  <span className={`sidebar-count-badge ${chatMenuBadges.group.toneClassName}`}>
                    {formatMenuBadgeCount(chatMenuBadges.group.count)}
                  </span>
                )}
              </Link>
            </li>
            )}
            {canBroadcast && (
            <li className="sidebar-item">
              <Link
                to="/admin/workspaces/communications/artist-chat/broadcast"
                className={isActivePath('/admin/workspaces/communications/artist-chat/broadcast', { exact: true }) ? 'is-active' : ''}
              >
                一斉送信
              </Link>
            </li>
            )}
          </ul>
            </>
          )}

          {(canViewCastingOffer || canViewCastingContract) && (
            <>
          <SectionLabel iconClass="fa-solid fa-handshake" text="キャスティング" />
          <ul>
        {canViewCastingOffer && (
        <SidebarItemWithCreateShortcut shortcut={createShortcuts.offers}>
          <Link
            to="/admin/workspaces/casting/offers"
            className={castingOfferIsActive ? 'is-active' : ''}
          >
            <span>オファー</span>
            {offerTaskCount > 0 && (
              <span
                className={`sidebar-count-badge ${resolveAdminAssignmentBadgeToneClass(offerTaskAssignedCount)}`}
              >
                {offerTaskCount > 99 ? '99+' : offerTaskCount}
              </span>
            )}
          </Link>
        </SidebarItemWithCreateShortcut>
        )}
        {canViewCastingContract && (
        <SidebarItemWithCreateShortcut shortcut={createShortcuts.contracts}>
          <Link
            to="/admin/workspaces/contracts/history"
            className={castingContractIsActive ? 'is-active' : ''}
          >
            <span>本契約</span>
            {contractTaskCount > 0 && (
              <span
                className={`sidebar-count-badge ${resolveAdminAssignmentBadgeToneClass(contractTaskAssignedCount)}`}
              >
                {contractTaskCount > 99 ? '99+' : contractTaskCount}
              </span>
            )}
          </Link>
        </SidebarItemWithCreateShortcut>
        )}
          </ul>
            </>
          )}

          {hasConciergePermission && (
            <>
          <SectionLabel iconClass="fa-solid fa-life-ring" text="サポート" />
          <ul>
        <li className="sidebar-item">
          <Link
            to="/admin/workspaces/concierge/dashboard"
            className={isActivePath('/admin/workspaces/concierge/dashboard', { exact: true }) ? 'is-active' : ''}
          >
            <span>コンシェルジュ</span>
            {conciergePendingCount > 0 && (
              <span className={`sidebar-count-badge ${conciergeBadgeToneClass}`}>
                {conciergePendingCount > 99 ? '99+' : conciergePendingCount}
              </span>
            )}
          </Link>
        </li>
          </ul>
            </>
          )}

          {(canViewTicketSales || canViewFinance || canViewPayments) && (
            <>
          <SectionLabel iconClass="fa-solid fa-money-check" text="経理管理" />
          <ul>
        {canViewTicketSales && (
        <li className="sidebar-item">
          <Link
            to="/admin/workspaces/event-operations/ticket-sales"
            className={isActivePath('/admin/workspaces/event-operations/ticket-sales', { exact: true }) ? 'is-active' : ''}
          >
            <span>チケット売上</span>
          </Link>
        </li>
        )}
        {canViewFinance && (
        <li className="sidebar-item">
          <Link
            to={financeLink()}
            className={isActivePath('/admin/workspaces/finance/dashboard') ? 'is-active' : ''}
          >
            <span>経理・集計</span>
          </Link>
        </li>
        )}
        {canViewPayments && (
        <li className="sidebar-item">
          <Link
            to="/admin/workspaces/finance/payments/monthly"
            className={isActivePath('/admin/workspaces/finance/payments/monthly', { exact: true }) ? 'is-active' : ''}
          >
            <span>請求一覧</span>
            {invoiceUndownloadedCount > 0 && (
              <span
                className={`sidebar-count-badge sidebar-count-badge--${invoiceUndownloadedTone || 'alert'}`}
              >
                {invoiceUndownloadedCount > 99 ? '99+' : invoiceUndownloadedCount}
              </span>
            )}
          </Link>
        </li>
        )}
          </ul>
            </>
          )}

          <SystemSettingsSection
            can={can}
            canManageAccounts={canManageAccounts}
            canManageMacRelease={canManageMacRelease}
            canManageMobileRelease={canManageMobileRelease}
            canManagePolicies={canManagePolicies}
            canViewActionLogs={canViewActionLogs}
            canViewAnnouncements={canViewAnnouncements}
            canViewBugs={canViewBugs}
            canViewOperationalEmails={canViewOperationalEmails}
            canManageServiceAiSettings={isExactAdminRole}
            canManageContractClauseSettings={isExactAdminRole}
            canManagePlayguides={isExactAdminRole}
            isSuperAdmin={isSuperAdmin}
            pendingApprovalCount={pendingApprovalCount}
            isActivePath={isActivePath}
          />
        </>
      )}

      <SidebarBottom onLogout={handleLogout} />
    </aside>
  );
}

export default Sidebar;
